import React from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown, Sparkles } from "lucide-react";
import ParticleNetwork from "../ParticleNetwork";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HEADLINE = ["Engineering Tomorrow's IT —", "Consulting, AI & Cloud,", "Delivered."];

export const Hero = () => {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 600], [0, 140]);
  const yContent = useTransform(scrollY, [0, 600], [0, 60]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section data-testid="hero-section" className="relative min-h-[100svh] flex items-center overflow-hidden bg-navy-deep">
      {/* particle network */}
      <motion.div style={{ y: yBg }} className="absolute inset-0 z-0">
        <ParticleNetwork className="w-full h-full" density={1.1} />
      </motion.div>

      {/* glow accents */}
      <div className="absolute top-1/4 -left-40 w-[500px] h-[500px] rounded-full bg-brand-orange/20 blur-[140px] z-0" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full bg-[#1c3a6e]/40 blur-[160px] z-0" />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-navy-deep/40 via-transparent to-navy-deep" />

      <motion.div style={{ y: yContent, opacity }} className="container-x relative z-10 pt-28 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-8"
        >
          <Sparkles size={14} className="text-brand-orange" />
          <span data-testid="hero-brand-badge" className="font-mono text-xs tracking-wide text-slate-300">ATS IT Solutions · Technology, AI & Digital Growth</span>
        </motion.div>

        <h1 className="font-head text-4xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02] max-w-5xl">
          {HEADLINE.map((l, i) => (
            <span key={i} className="block overflow-hidden py-1">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="show"
                className={`block ${i === 2 ? "text-gradient-orange" : "text-white"}`}
              >
                {l}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-8 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed"
        >
          <span data-testid="hero-positioning" className="block text-white font-medium mb-2">Technology, AI &amp; Digital Growth Solutions for Modern Businesses.</span>
          From software and cloud to AI automation and digital growth, ATS IT Solutions helps businesses build, modernize, automate, secure and scale — as one accountable, enterprise-grade partner.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <Link to="/contact" data-testid="hero-cta-primary" className="btn-primary text-base">
            Get a Free Consultation <ArrowUpRight size={18} />
          </Link>
          <a
            href="#services"
            data-testid="hero-cta-secondary"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById("services");
              if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
              else el?.scrollIntoView({ behavior: "smooth" });
            }}
            className="btn-secondary text-base"
          >
            Explore Services
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-slate-500"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.div>
    </section>
  );
};

export default Hero;
